import { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Mail, Phone, Trash2, Shield, Edit2, Eye, EyeOff, Camera, DollarSign, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmployeesPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'DJ',
    role_id: '',
    password: '',
    pay_type: 'hourly',
    hourly_rate: '',
    flat_rate: '',
    skills: [],
    notes: '',
    photo_url: ''
  });
  const [skillInput, setSkillInput] = useState('');

  useEffect(() => {
    fetchEmployees();
    fetchRoles();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : null;
  };

  const getRoleColor = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.color : '#64748b';
  };

  const resetForm = () => {
    setNewEmployee({
      name: '',
      email: '',
      phone: '',
      role: 'DJ',
      role_id: '',
      password: '',
      pay_type: 'hourly',
      hourly_rate: '',
      flat_rate: '',
      skills: [],
      notes: '',
      photo_url: ''
    });
    setEditingEmployee(null);
    setShowPassword(false);
  };

  const openEditDialog = (employee) => {
    setEditingEmployee(employee);
    setNewEmployee({
      name: employee.name,
      email: employee.email,
      phone: employee.phone || '',
      role: employee.role,
      role_id: employee.role_id || '',
      password: '', // Don't show existing password
      pay_type: employee.pay_type || 'hourly',
      hourly_rate: employee.hourly_rate || '',
      flat_rate: employee.flat_rate || '',
      skills: employee.skills || [],
      notes: employee.notes || '',
      photo_url: employee.photo_url || ''
    });
    setShowDialog(true);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Data = event.target.result;
        
        if (editingEmployee) {
          // Upload directly if editing existing employee
          await axios.post(`${API}/employees/${editingEmployee.id}/photo`, {
            photo_url: base64Data
          });
          toast.success('Photo uploaded');
          fetchEmployees();
        }
        
        // Update form state
        setNewEmployee(prev => ({ ...prev, photo_url: base64Data }));
        setUploadingPhoto(false);
      };
      reader.onerror = () => {
        toast.error('Failed to read image');
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload photo');
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    if (editingEmployee) {
      try {
        await axios.delete(`${API}/employees/${editingEmployee.id}/photo`);
        toast.success('Photo removed');
        fetchEmployees();
      } catch (error) {
        toast.error('Failed to remove photo');
        return;
      }
    }
    setNewEmployee(prev => ({ ...prev, photo_url: '' }));
  };

  const handleCreate = async () => {
    if (!newEmployee.name || !newEmployee.email || !newEmployee.role) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const payload = { ...newEmployee };
      // Only send password if it's set
      if (!payload.password) {
        delete payload.password;
      }
      // Convert hourly_rate to number if present
      if (payload.hourly_rate) {
        payload.hourly_rate = parseFloat(payload.hourly_rate);
      } else {
        payload.hourly_rate = null;
      }
      // Convert flat_rate to number if present
      if (payload.flat_rate) {
        payload.flat_rate = parseFloat(payload.flat_rate);
      } else {
        payload.flat_rate = null;
      }

      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, payload);
        toast.success('Employee updated successfully');
      } else {
        await axios.post(`${API}/employees`, payload);
        toast.success('Employee added successfully');
      }
      setShowDialog(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(editingEmployee ? 'Failed to update employee' : 'Failed to add employee');
    }
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;

    try {
      await axios.delete(`${API}/employees/${employeeId}`);
      toast.success('Employee deleted');
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };

  const addSkill = () => {
    if (skillInput.trim() && !newEmployee.skills.includes(skillInput.trim())) {
      setNewEmployee({
        ...newEmployee,
        skills: [...newEmployee.skills, skillInput.trim()]
      });
      setSkillInput('');
    }
  };

  const removeSkill = (skill) => {
    setNewEmployee({
      ...newEmployee,
      skills: newEmployee.skills.filter(s => s !== skill)
    });
  };

  return (
    <div className="flex min-h-screen bg-obsidian" data-testid="employees-page">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-heading text-4xl font-bold mb-2" data-testid="employees-heading">Employees</h1>
              <p className="text-muted-foreground">Manage your DJ team members</p>
            </div>
            <Dialog open={showDialog} onOpenChange={(open) => {
              setShowDialog(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-neon-blue hover:bg-neon-blue/80 text-black" data-testid="create-employee-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Employee
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-employee-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-foreground">
                    {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  {/* Photo Upload Section */}
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      {newEmployee.photo_url ? (
                        <img 
                          src={newEmployee.photo_url} 
                          alt="Employee" 
                          className="w-20 h-20 rounded-full object-cover border-2 border-border"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center border-2 border-dashed border-border">
                          <User className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                      <label className="absolute -bottom-1 -right-1 w-7 h-7 bg-neon-blue rounded-full flex items-center justify-center cursor-pointer hover:bg-neon-blue/80 transition-colors">
                        <Camera className="w-4 h-4 text-black" />
                        <input 
                          type="file" 
                          accept="image/*" 
                          className="hidden" 
                          onChange={handlePhotoUpload}
                          disabled={uploadingPhoto}
                        />
                      </label>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">Profile Photo</p>
                      <p className="text-xs text-muted-foreground">Upload a photo (max 2MB)</p>
                      {newEmployee.photo_url && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={handleRemovePhoto}
                          className="text-red-500 hover:text-red-400 mt-1 h-7 px-2"
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> Remove
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-foreground">Name *</Label>
                      <Input
                        value={newEmployee.name}
                        onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                        className="bg-background border-border text-foreground"
                        data-testid="employee-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">Email *</Label>
                      <Input
                        type="email"
                        value={newEmployee.email}
                        onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                        className="bg-background border-border text-foreground"
                        data-testid="employee-email-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-foreground">Phone</Label>
                      <Input
                        value={newEmployee.phone}
                        onChange={(e) => setNewEmployee({ ...newEmployee, phone: e.target.value })}
                        className="bg-background border-border text-foreground"
                        data-testid="employee-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">Job Title *</Label>
                      <Select value={newEmployee.role} onValueChange={(v) => setNewEmployee({ ...newEmployee, role: v })}>
                        <SelectTrigger className="bg-background border-border text-foreground" data-testid="employee-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="DJ">DJ</SelectItem>
                          <SelectItem value="MC">MC / Host</SelectItem>
                          <SelectItem value="Technician">Sound Technician</SelectItem>
                          <SelectItem value="Lighting">Lighting Operator</SelectItem>
                          <SelectItem value="Assistant">Assistant</SelectItem>
                          <SelectItem value="Manager">Manager</SelectItem>
                          <SelectItem value="Sales">Sales</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Permission Role Selection */}
                  <div className="space-y-2">
                    <Label className="text-foreground flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Permission Role
                    </Label>
                    <Select 
                      value={newEmployee.role_id || "none"} 
                      onValueChange={(v) => setNewEmployee({ ...newEmployee, role_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger className="bg-background border-border text-foreground" data-testid="employee-permission-role-select">
                        <SelectValue placeholder="Select a permission role..." />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="none">No Role (Owner access only)</SelectItem>
                        {roles.map((role) => (
                          <SelectItem key={role.id} value={role.id}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: role.color }}
                              />
                              {role.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Determines what this employee can access when logging in
                    </p>
                  </div>

                  {/* Password for Employee Login */}
                  <div className="space-y-2">
                    <Label className="text-foreground">
                      {editingEmployee ? 'Password (leave blank to keep current)' : 'Password (for employee login)'}
                    </Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={newEmployee.password}
                        onChange={(e) => setNewEmployee({ ...newEmployee, password: e.target.value })}
                        placeholder={editingEmployee ? '••••••••' : 'Set login password'}
                        className="bg-background border-border text-foreground pr-10"
                        data-testid="employee-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Employee can log in with their email and this password
                    </p>
                  </div>

                  {/* Pay Rate Section */}
                  <div className="p-4 rounded-lg bg-muted/30 border border-border space-y-3">
                    <Label className="text-foreground flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Pay Rate
                    </Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="pay_type"
                          value="hourly"
                          checked={newEmployee.pay_type === 'hourly'}
                          onChange={(e) => setNewEmployee({ ...newEmployee, pay_type: e.target.value })}
                          className="text-neon-blue"
                        />
                        <span className="text-sm text-foreground">Hourly Rate</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="pay_type"
                          value="flat"
                          checked={newEmployee.pay_type === 'flat'}
                          onChange={(e) => setNewEmployee({ ...newEmployee, pay_type: e.target.value })}
                          className="text-neon-blue"
                        />
                        <span className="text-sm text-foreground">Flat Rate per Event</span>
                      </label>
                    </div>
                    
                    {newEmployee.pay_type === 'hourly' ? (
                      <div className="space-y-1">
                        <Label className="text-sm text-muted-foreground">Hourly Rate ($)</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                          <Input
                            type="number"
                            value={newEmployee.hourly_rate}
                            onChange={(e) => setNewEmployee({ ...newEmployee, hourly_rate: e.target.value })}
                            className="bg-background border-border text-foreground pl-7"
                            placeholder="0.00"
                            step="0.01"
                            data-testid="employee-hourly-rate-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">/hr</span>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <Label className="text-sm text-muted-foreground">Flat Rate per Event ($)</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                          <Input
                            type="number"
                            value={newEmployee.flat_rate}
                            onChange={(e) => setNewEmployee({ ...newEmployee, flat_rate: e.target.value })}
                            className="bg-background border-border text-foreground pl-7"
                            placeholder="0.00"
                            step="0.01"
                            data-testid="employee-flat-rate-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">/event</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">Skills</Label>
                    <div className="flex gap-2">
                      <Input
                        value={skillInput}
                        onChange={(e) => setSkillInput(e.target.value)}
                        placeholder="e.g., Hip Hop, Weddings, Turntablism"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                        className="bg-background border-border text-foreground"
                        data-testid="employee-skill-input"
                      />
                      <Button onClick={addSkill} type="button" data-testid="add-skill-btn">
                        Add
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {newEmployee.skills.map((skill, index) => (
                        <div
                          key={index}
                          className="bg-primary/20 text-primary px-3 py-1 rounded-full text-sm flex items-center gap-2"
                          data-testid={`skill-tag-${index}`}
                        >
                          {skill}
                          <button
                            onClick={() => removeSkill(skill)}
                            className="hover:text-primary/70"
                            data-testid={`remove-skill-btn-${index}`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">Notes</Label>
                    <textarea
                      value={newEmployee.notes}
                      onChange={(e) => setNewEmployee({ ...newEmployee, notes: e.target.value })}
                      className="w-full min-h-[80px] bg-background border border-border rounded-lg p-3 text-foreground"
                      data-testid="employee-notes-input"
                    />
                  </div>
                  <Button
                    onClick={handleCreate}
                    className="w-full bg-neon-blue hover:bg-neon-blue/80 text-black"
                    data-testid="employee-create-submit-btn"
                  >
                    {editingEmployee ? 'Update Employee' : 'Add Employee'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : employees.length === 0 ? (
            <div className="text-center py-12 glass-card rounded-3xl" data-testid="no-employees-message">
              <p className="text-muted-foreground">No employees yet. Add your first team member!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="employees-list">
              {employees.map((employee) => (
                <div
                  key={employee.id}
                  className="glass-card-hover rounded-2xl p-6"
                  data-testid={`employee-item-${employee.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {/* Employee Photo */}
                      {employee.photo_url ? (
                        <img 
                          src={employee.photo_url} 
                          alt={employee.name} 
                          className="w-14 h-14 rounded-full object-cover border-2 border-border"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center border-2 border-border">
                          <User className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-heading text-xl font-semibold mb-1">{employee.name}</h3>
                        <span className="text-sm text-neon-blue">{employee.role}</span>
                        {employee.role_id && getRoleName(employee.role_id) && (
                          <div className="mt-1">
                            <span 
                              className="text-xs px-2 py-0.5 rounded-full"
                              style={{ 
                                backgroundColor: `${getRoleColor(employee.role_id)}20`,
                                color: getRoleColor(employee.role_id)
                              }}
                            >
                              <Shield className="w-3 h-3 inline mr-1" />
                              {getRoleName(employee.role_id)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(employee)}
                        className="text-muted-foreground hover:text-foreground"
                        data-testid={`edit-employee-btn-${employee.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(employee.id)}
                        className="text-red-500 hover:text-red-400"
                        data-testid={`delete-employee-btn-${employee.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="w-4 h-4" />
                      <span>{employee.email}</span>
                    </div>
                    {employee.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="w-4 h-4" />
                        <span>{employee.phone}</span>
                      </div>
                    )}
                    {/* Pay Rate Display */}
                    {(employee.hourly_rate || employee.flat_rate) && (
                      <div className="text-muted-foreground flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        {employee.pay_type === 'flat' && employee.flat_rate ? (
                          <span>
                            <span className="text-foreground font-semibold">${employee.flat_rate}</span>
                            <span className="text-xs">/event</span>
                          </span>
                        ) : employee.hourly_rate ? (
                          <span>
                            <span className="text-foreground font-semibold">${employee.hourly_rate}</span>
                            <span className="text-xs">/hr</span>
                          </span>
                        ) : null}
                      </div>
                    )}
                    {employee.password_hash && (
                      <div className="text-xs text-green-500 flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        Login enabled
                      </div>
                    )}
                    {employee.skills && employee.skills.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {employee.skills.map((skill, idx) => (
                          <span
                            key={idx}
                            className="bg-white/5 px-2 py-1 rounded text-xs"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
